<template>
  <div :class="$style.controls">
    <button
      ref="addTaskButtonRef"
      @click="handleAddTaskButtonClick"
      :class="$style.addTaskButton"
    >
      <PlusIcon :size="16" />
      Add Task
    </button>
    <div class="flex-grow-1"></div>
    <div :class="$style.controlsRight">
      <button @click="$emit('showFilter')" :class="$style.filterButton">
        <FilterIcon :size="16" />
      </button>
      <button
        @click="$emit('toggleViewMode')"
        :class="$style.viewToggleButton"
        :title="
          viewMode === ViewMode.COMPRESSED
            ? 'Switch to Timeline View'
            : 'Switch to Compressed View'
        "
      >
        <ViewCompressedIcon
          v-if="viewMode === ViewMode.COMPRESSED"
          :size="16"
        />
        <ViewTimelineIcon v-else :size="16" />
      </button>
      <button
        @click="$emit('showAnalytics')"
        :class="$style.analyticsButton"
        title="View Analytics"
      >
        <AnalyticsIcon :size="16" />
      </button>
      <button :class="$style.actionsButton" @click="$emit('showActions')">
        <MoreIcon :size="16" />
      </button>
    </div>

    <!-- Guide popover for Add Task button when no tasks exist -->
    <GuidePopover
      :is-visible="showAddTaskGuide"
      :text="addTaskGuideText"
      :target-element="addTaskButtonRef || undefined"
      position="bottom"
      :z-index="100"
      @close="closeAddTaskGuide"
      @dismiss="dismissAddTaskGuide"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import {
  PlusIcon,
  FilterIcon,
  ViewCompressedIcon,
  ViewTimelineIcon,
  AnalyticsIcon,
  MoreIcon,
} from "../icons";
import GuidePopover from "../GuidePopover.vue";
import { ViewMode } from "../../types/enums";

interface Props {
  viewMode: ViewMode;
  showAddTaskGuide: boolean;
  addTaskGuideText: string;
}

interface Emits {
  (e: "showFilter"): void;
  (e: "toggleViewMode"): void;
  (e: "showAnalytics"): void;
  (e: "showActions"): void;
  (e: "addTask"): void;
  (e: "closeGuide"): void;
  (e: "dismissGuide"): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const addTaskButtonRef = ref<HTMLElement | null>(null);

function handleAddTaskButtonClick() {
  emit("addTask");
}

function closeAddTaskGuide() {
  emit("closeGuide");
}

function dismissAddTaskGuide() {
  emit("dismissGuide");
}
</script>

<style module>
.controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.flex-grow-1 {
  flex-grow: 1;
}

.controlsRight {
  display: flex;
  align-items: center;
  gap: 8px;
}

.addTaskButton {
  background-color: transparent;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  height: 42px;
  min-width: 90px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1em;
  padding: 0;
}

.addTaskButton:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.filterButton {
  background-color: transparent;
  padding: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filterButton:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.actionsButton {
  background-color: transparent;
  padding: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.actionsButton:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.viewToggleButton {
  background-color: transparent;
  padding: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewToggleButton:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.analyticsButton {
  background-color: transparent;
  padding: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.analyticsButton:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

/* Responsive adjustments for smaller screens */
@media (max-width: 768px) {
  .controls {
    gap: 8px;
  }

  .filterButton {
    width: 38px;
    height: 38px;
    padding: 0;
  }

  .actionsButton {
    width: 38px;
    height: 38px;
    padding: 0;
  }

  .addTaskButton {
    height: 38px;
  }
}

@media (max-width: 480px) {
  .controls {
    gap: 6px;
  }

  .filterButton {
    width: 34px;
    height: 34px;
    padding: 0;
  }

  .actionsButton {
    width: 34px;
    height: 34px;
    padding: 0;
  }

  .addTaskButton {
    height: 34px;
  }
}
</style>
